import posthog from 'posthog-js';
import logger from '@/util/logging/logger.js';

let initialized = false;
const isBrowser = typeof window !== 'undefined';

const envBool = (v) => String(v ?? '').toLowerCase() === 'true';
const isDev = import.meta.env.MODE === 'development' || import.meta.env.DEV === true;

const ENABLE_IN_DEV = envBool(import.meta.env.VITE_PH_ENABLE_IN_DEV);
const DISABLE_FLAG = envBool(import.meta.env.VITE_PH_DISABLE);
const DISABLE_ERRORS = envBool(import.meta.env.VITE_PH_DISABLE_ERRORS);

const DISABLE_ALL = (isDev && !ENABLE_IN_DEV) || DISABLE_FLAG;

const normalizeHost = (h) => {
	const s = (h || '').trim();
	if (!s) return '';
	return s.startsWith('http://') || s.startsWith('https://') ? s : `https://${s}`;
};
const POSTHOG_KEY = (import.meta.env.VITE_POSTHOG_KEY || '').trim();
const POSTHOG_HOST = normalizeHost(import.meta.env.VITE_POSTHOG_HOST);

const noop = () => {};
const noopClient = {
	capture: noop,
	captureException: noop,
	identify: noop,
	group: noop,
	alias: noop,
	reset: noop,
	opt_in_capturing: noop,
	opt_out_capturing: noop,
	get_distinct_id: () => 'disabled',
	register: noop,
	unregister: noop
};

const SHOULD_INIT = isBrowser && !DISABLE_ALL && !!POSTHOG_KEY;

if (isBrowser && !DISABLE_ALL && !POSTHOG_KEY) {
	logger.warn('[PH] VITE_POSTHOG_KEY is missing/empty; PostHog will be disabled');
}

if (SHOULD_INIT && !initialized) {
	posthog.init(POSTHOG_KEY, {
		api_host: POSTHOG_HOST || undefined,
		capture_pageview: true,
		capture_pageleave: true,
		autocapture: true,
		// Application handlers attach operation and endpoint context. Keeping SDK
		// exception autocapture off prevents the same error from creating a second,
		// context-free issue before those handlers run.
		capture_exceptions: false
		// start simple; add session recording later
	});

	// Provide captureException shim if you want it
	if (typeof posthog.captureException !== 'function') {
		posthog.captureException = function (error, props = {}) {
			const err = error instanceof Error ? error : new Error(String(error));
			posthog.capture('$exception', {
				$exception_message: err.message,
				$exception_type: err.name || 'Error',
				$exception_stack_trace: err.stack,
				...props
			});
		};
	}
	if (DISABLE_ERRORS) posthog.captureException = noop;
	initialized = true;
}

const client = SHOULD_INIT ? posthog : noopClient;
export default client;

export function identifyUser({ userNumber, username, properties = {}, doAlias = true } = {}) {
	const distinctId = userNumber != null ? String(userNumber) : (username || '').trim();
	if (!distinctId) {
		logger.warn('[PH] identifyUser skipped: no distinctId (userNumber/username missing)');
		return;
	}
	try {
		const before = client.get_distinct_id?.();
		const aliasKey = `ph_alias_done_${distinctId}`;
		if (doAlias && before && before !== distinctId && !localStorage.getItem(aliasKey)) {
			client.alias?.(distinctId, before);
			localStorage.setItem(aliasKey, '1');
		}
		client.identify(distinctId, { username, ...properties });

		let after = client.get_distinct_id?.();
		if (after !== distinctId) {
			if (import.meta.env.DEV) {
				logger.debug('[PH] identify did not flip distinct_id: forcing reset+identify', { before, distinctId, after });
			}
			client.reset();
			client.identify(distinctId, { username, ...properties });
			after = client.get_distinct_id?.();
		}
		if (import.meta.env.DEV) {
			logger.debug('[PH] identifyUser', { before, distinctId, after, properties });
		}
	} catch (e) {
		logger.warn('[PH] identifyUser error', { error: e });
	}
}

export function resetPostHog() {
	client.reset();
}
